import { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  LogEntry,
  LogFilters,
  LogPagination,
  LogResponse,
  DEFAULT_FILTERS,
} from './types'
import { fetchLogs } from './services/logService'

interface UseLogViewerReturn {
  entries: LogEntry[]
  pagination: LogPagination | null
  filters: LogFilters
  loading: boolean
  // True while any request is in flight, including background refetches of
  // the current page (manual refresh, auto-refresh) that don't show the
  // skeleton. Drives the refresh button's spinner.
  refreshing: boolean
  error: string | null
  expandedEntryId: string | null
  lastFetchedAt: number | null
  setFilters: (
    updater: Partial<LogFilters> | ((prev: LogFilters) => LogFilters),
  ) => void
  resetFilters: () => void
  setExpandedEntryId: (id: string | null) => void
  refresh: () => void
}

const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000
const DEBOUNCE_MS = 300

function filtersToParams(filters: LogFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.level !== 'ALL') params.set('level', filters.level)
  for (const subsystem of filters.subsystems)
    params.append('subsystem', subsystem)
  if (filters.datePreset !== 'last30d')
    params.set('datePreset', filters.datePreset)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  for (const eventType of filters.eventTypes)
    params.append('eventType', eventType)
  for (const user of filters.users) params.append('user', user)
  for (const state of filters.states) params.append('state', state)
  for (const entityType of filters.entityTypes)
    params.append('entityType', entityType)
  for (const outcome of filters.outcomes) params.append('outcome', outcome)
  if (filters.traceId) params.set('traceId', filters.traceId)
  if (filters.requestId) params.set('requestId', filters.requestId)
  if (filters.sortField !== 'timestamp')
    params.set('sortField', filters.sortField)
  if (filters.sortOrder !== 'newest') params.set('sortOrder', filters.sortOrder)
  if (filters.page !== 1) params.set('page', String(filters.page))
  if (filters.limit !== 25) params.set('limit', String(filters.limit))
  return params
}

function paramsToFilters(searchString: string): LogFilters {
  const params = new URLSearchParams(searchString)
  return {
    search: params.get('search') || DEFAULT_FILTERS.search,
    level:
      (params.get('level') as LogFilters['level']) || DEFAULT_FILTERS.level,
    subsystems: params.getAll('subsystem'),
    datePreset:
      (params.get('datePreset') as LogFilters['datePreset']) ||
      DEFAULT_FILTERS.datePreset,
    dateFrom: params.get('dateFrom') || DEFAULT_FILTERS.dateFrom,
    dateTo: params.get('dateTo') || DEFAULT_FILTERS.dateTo,
    eventTypes: params.getAll('eventType') as LogFilters['eventTypes'],
    users: params.getAll('user'),
    states: params.getAll('state'),
    entityTypes: params.getAll('entityType'),
    outcomes: params.getAll('outcome') as LogFilters['outcomes'],
    traceId: params.get('traceId') || DEFAULT_FILTERS.traceId,
    requestId: params.get('requestId') || DEFAULT_FILTERS.requestId,
    sortField:
      (params.get('sortField') as LogFilters['sortField']) ||
      DEFAULT_FILTERS.sortField,
    sortOrder:
      (params.get('sortOrder') as LogFilters['sortOrder']) ||
      DEFAULT_FILTERS.sortOrder,
    page: parseInt(params.get('page') || String(DEFAULT_FILTERS.page), 10),
    limit: parseInt(params.get('limit') || String(DEFAULT_FILTERS.limit), 10),
  }
}

export default function useLogViewer(): UseLogViewerReturn {
  const history = useHistory()
  const location = useLocation()

  const [filters, setFiltersState] = useState<LogFilters>(() =>
    paramsToFilters(location.search),
  )
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null)
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  // Debounce the filters used as the query key so rapid keystrokes in the
  // search input coalesce into one request instead of one per character.
  const [debouncedFilters, setDebouncedFilters] = useState(filters)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedFilters(filters), DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [filters])

  // Each distinct filter combination is its own cache entry, so returning to
  // a previously viewed page renders instantly from cache. keepPreviousData
  // keeps the previous response around while the next one loads — not to show
  // its rows (the view renders a skeleton instead), but so the pagination
  // footer stays mounted with a stable page count. TanStack Query discards
  // out-of-order responses so a slow request for old filters can never
  // overwrite results for newer ones.
  const {
    data: rawData,
    error,
    isPending,
    isPlaceholderData,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ['logs', debouncedFilters],
    queryFn: async () => {
      const response = await fetchLogs(debouncedFilters)

      const { totalPages } = response.pagination
      if (totalPages > 0 && debouncedFilters.page > totalPages) {
        setFiltersState(prev =>
          prev.page > totalPages ? { ...prev, page: totalPages } : prev,
        )
      }
      return response
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchInterval: AUTO_REFRESH_INTERVAL,
  })

  const data: LogResponse | undefined = rawData

  // Sync filters → URL query params
  useEffect(() => {
    const params = filtersToParams(filters)
    const search = params.toString()
    const currentSearch = location.search.replace(/^\?/, '')
    if (search !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      })
    }
  }, [filters, history, location.pathname, location.search])

  useEffect(() => {
    const unlisten = history.listen((loc, action) => {
      if (action !== 'POP') return
      const parsed = paramsToFilters(loc.search)
      const incomingParams = filtersToParams(parsed).toString()
      const currentParams = filtersToParams(filtersRef.current).toString()
      if (incomingParams !== currentParams) setFiltersState(parsed)
    })
    return unlisten
  }, [history])

  const setFilters = useCallback(
    (updater: Partial<LogFilters> | ((prev: LogFilters) => LogFilters)) => {
      setFiltersState(prev => {
        const next =
          typeof updater === 'function'
            ? updater(prev)
            : { ...prev, ...updater, page: 1 }
        return next
      })
    },
    [],
  )

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS)
  }, [])

  const refresh = useCallback(() => {
    void refetch()
  }, [refetch])

  return {
    entries: data?.data ?? [],
    pagination:
      data != null ? { ...data.pagination, page: filters.page } : null,
    filters,
    loading: isPending || (isPlaceholderData && isFetching),
    refreshing: isFetching,
    error:
      error != null
        ? error instanceof Error
          ? error.message
          : 'Failed to fetch logs'
        : null,
    expandedEntryId,
    lastFetchedAt: dataUpdatedAt > 0 ? dataUpdatedAt : null,
    setFilters,
    resetFilters,
    setExpandedEntryId,
    refresh,
  }
}
